import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import sampleAvatar from 'assets/red_avatar.jpeg';
import { HandHistoryContext } from 'contexts/HandHistoryContext';
import { useHandHistory } from 'hooks/useHandHistory';

import { handHistoryScenarios } from './__fixtures__/handHistory.fixtures';
import {
  DEFAULT_PREVIEW_PLAYER_COUNT,
  withPreviewPlayerCount
} from './__fixtures__/previewPlayerCount';
import { getHandHistoryLayout, HandHistoryLayout } from './HandHistoryLayouts';
import { HandHistorySnapshot } from './types';

// Local sample artwork also makes the Product HTML independent of the media CDN.
const withLocalAvatars = (hand: HandHistorySnapshot): HandHistorySnapshot => ({
  ...hand,
  tableSnapshot: {
    ...hand.tableSnapshot,
    seats: hand.tableSnapshot.seats.map((seat) => ({ ...seat, avatar: sampleAvatar }))
  },
  potGroups: hand.potGroups.map((pot) => ({
    ...pot,
    players: pot.players.map((player) => ({ ...player, avatar: sampleAvatar }))
  })),
  pots: hand.pots?.map((pot) => ({
    ...pot,
    awards: pot.awards.map((award) => ({
      ...award,
      players: award.players.map((player) => ({ ...player, avatar: sampleAvatar }))
    }))
  }))
});

const DemoPlayerCountContext = createContext<{
  layout: HandHistoryLayout;
  demoPlayerCount: number | undefined;
  setDemoPlayerCount: React.Dispatch<React.SetStateAction<number | undefined>>;
} | null>(null);

export const useDemoPlayerCount = () => {
  const value = useContext(DemoPlayerCountContext);
  if (!value) throw new Error('Player-count controls require HandHistoryDemoProvider');
  return value;
};

// Demo #2 (2026-09-24, HH-03): the live hand can play forward a row at a time
// so the list's scroll hold can be watched with rows arriving above the reader.
export const LIVE_HAND_STEP_MS = 2000;

const DemoLiveHandContext = createContext<{
  livePlaying: boolean;
  liveDone: boolean;
  setLivePlaying: (playing: boolean) => void;
  restartLiveHand: () => void;
} | null>(null);

export const useDemoLiveHand = () => {
  const value = useContext(DemoLiveHandContext);
  if (!value) throw new Error('Live-hand controls require HandHistoryDemoProvider');
  return value;
};

const latestBoard = (events: HandHistorySnapshot['playByPlay']) =>
  [...events].reverse().find((event) => event.tableCards?.length)?.tableCards ?? [];

// Only mounted by /hand-history-playground. The live provider stays unchanged.
export const HandHistoryDemoProvider = ({ children }: { children: React.ReactNode }) => {
  const base = useHandHistory();
  // Six-handed by default: the demo has to show reveal/muck behaviour at a
  // fuller table (Cory, internal demo 2026-09-23). Scenarios that need fewer
  // players are padded with pre-flop folders by withPreviewPlayerCount.
  const [demoPlayerCount, setDemoPlayerCount] = useState<number | undefined>(
    DEFAULT_PREVIEW_PLAYER_COUNT
  );
  const { search } = useLocation();
  const layout = getHandHistoryLayout(search);
  const activeSample = useMemo(() => {
    const mode = base.demoVariant === 'texas' ? base.demoGameMode : 'cash';
    const original = handHistoryScenarios[mode][base.demoVariant];
    return withPreviewPlayerCount(original.completedHands[0], demoPlayerCount);
  }, [base.demoGameMode, base.demoVariant, demoPlayerCount]);

  // The live hand opens at the flop, as the fixture does, and plays on from there.
  const [liveStep, setLiveStep] = useState(0);
  const [livePlaying, setLivePlaying] = useState(false);
  const flopEnd = activeSample.playByPlay.findIndex((event) => event.tableCards?.length === 3) + 1;
  const liveEnd = Math.min(flopEnd + liveStep, activeSample.playByPlay.length);
  const liveDone = liveEnd >= activeSample.playByPlay.length;

  useEffect(() => setLiveStep(0), [activeSample.handId]);
  useEffect(() => {
    if (liveDone) setLivePlaying(false);
  }, [liveDone]);
  useEffect(() => {
    if (!livePlaying) return undefined;
    const timer = window.setInterval(() => setLiveStep((step) => step + 1), LIVE_HAND_STEP_MS);
    return () => window.clearInterval(timer);
  }, [livePlaying]);

  const data = useMemo(() => {
    const liveEvents = activeSample.playByPlay.slice(0, liveEnd);
    return {
      ...base.data,
      completedHands: base.data.completedHands.map((hand) =>
        withLocalAvatars(
          withPreviewPlayerCount(
            layout === 'current'
              ? hand
              : {
                  ...hand,
                  playByPlay: hand.playByPlay.map((event) => {
                    const run = /^Dealing run (\d+)/.exec(event.text);
                    const board = run && hand.boards?.[Number(run[1]) - 1];
                    return board
                      ? { ...event, text: `Dealing run ${run[1]}`, tableCards: board }
                      : event;
                  })
                },
            demoPlayerCount
          )
        )
      ),
      activeHand:
        base.data.activeHand && demoPlayerCount !== undefined
          ? {
              ...base.data.activeHand,
              board: latestBoard(liveEvents),
              playByPlay: liveEvents.map((event, index) => ({
                ...event,
                id: `active-${index}`,
                ts: base.data.activeHand?.playByPlay[0]?.ts
                  ? base.data.activeHand.playByPlay[0].ts + index * 2000
                  : undefined
              }))
            }
          : base.data.activeHand
    };
  }, [activeSample, base.data, demoPlayerCount, layout, liveEnd]);

  const liveHand = useMemo(
    () => ({
      livePlaying,
      liveDone,
      setLivePlaying,
      restartLiveHand: () => setLiveStep(0)
    }),
    [liveDone, livePlaying]
  );

  return (
    <DemoPlayerCountContext.Provider value={{ demoPlayerCount, setDemoPlayerCount, layout }}>
      <DemoLiveHandContext.Provider value={liveHand}>
        <HandHistoryContext.Provider value={{ ...base, data }}>
          {children}
        </HandHistoryContext.Provider>
      </DemoLiveHandContext.Provider>
    </DemoPlayerCountContext.Provider>
  );
};
